/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitch from "@/components/LangSwitch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dashboardPath(role) {
  if (role === "owner") return "/dashboard/owner";
  if (role === "trainer") return "/dashboard/trainer";
  return "/dashboard/client";
}

export default function Login() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [twofa, setTwofa] = useState("");

  if (user) return <Navigate to={dashboardPath(user.role)} replace />;

  const onPasswordStep = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim().toLowerCase(), password }, { withCredentials: true });
      if (data.requires_2fa) { setChallengeToken(data.challenge_token); setStep(2); setTwofa(""); }
      else { const u = await refresh(); navigate(dashboardPath(u?.role || data.role), { replace: true }); }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const onTotpStep = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { challenge_token: challengeToken, code: twofa }, { withCredentials: true });
      const u = await refresh();
      navigate(dashboardPath(u?.role || data.role), { replace: true });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-stretch">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#1C3A27] text-white p-12 relative overflow-hidden">
        <div aria-hidden className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full opacity-[0.08] bg-[radial-gradient(#FAFAF7_1px,transparent_1px)] [background-size:14px_14px]" />
        <div className="flex items-center justify-between relative">
          <Link to="/" className="flex items-center gap-2 group w-fit" data-testid="login-back-home">
            <span className="w-8 h-8 rounded-full bg-white grid place-items-center"><span className="w-3 h-3 rounded-full bg-[#E05A3D]" /></span>
            <span className="font-display text-xl font-bold tracking-tight">Mazra3ti</span>
          </Link>
          <LangSwitch />
        </div>
        <div className="relative">
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">{t.app.login.side_overline}</div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-black leading-tight">
            {t.app.login.side_title_a}<br /> {t.app.login.side_title_b}
          </h1>
          <p className="mt-5 text-white/70 text-lg max-w-md">{t.app.login.side_desc}</p>
        </div>
        <div className="relative text-sm text-white/60">© {new Date().getFullYear()} Mazra3ti</div>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 py-12 relative">
        <div className="lg:hidden flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#5C6B5C]">
            <ArrowLeft size={16} /> {t.app.common.back_home}
          </Link>
          <LangSwitch variant="light" />
        </div>

        <div className="max-w-md w-full mx-auto">
          {step === 1 ? (
            <>
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.login.title}</h2>
              <p className="mt-2 text-[#5C6B5C]">{t.app.login.subtitle}</p>
              <form onSubmit={onPasswordStep} className="mt-8 space-y-5" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-[#1C3A27] font-medium">{t.app.login.email}</Label>
                  <Input id="email" data-testid="login-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.app.login.email_ph} className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D]" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[#1C3A27] font-medium">{t.app.login.password}</Label>
                  <div className="relative mt-2">
                    <Input id="password" data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D] pe-10" />
                    <button type="button" onClick={() => setShow((v) => !v)} data-testid="login-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B5C] hover:text-[#1C3A27]">
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && <div data-testid="login-error" role="alert" className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
                <Button type="submit" data-testid="login-submit" disabled={submitting} className="w-full h-12 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold text-base">
                  {submitting ? t.app.login.submitting : t.app.login.submit}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#1C3A27] grid place-items-center"><ShieldCheck size={24} className="text-[#E05A3D]" /></div>
                <div>
                  <h2 className="font-display text-2xl md:text-3xl font-black text-[#1C3A27]">{t.app.login.twofa_title}</h2>
                  <p className="text-[#5C6B5C] text-sm">{t.app.login.twofa_desc}</p>
                </div>
              </div>
              <form onSubmit={onTotpStep} className="mt-6 space-y-5" data-testid="totp-form">
                <div>
                  <Label className="text-[#1C3A27] font-medium">{t.app.login.twofa_label}</Label>
                  <Input data-testid="totp-code" value={twofa} onChange={(e) => setTwofa(e.target.value)} autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={20} placeholder={t.app.login.twofa_ph} className="mt-2 h-14 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D] text-center text-lg font-mono tracking-widest" />
                  <p className="mt-2 text-xs text-[#5C6B5C] flex items-center gap-1.5"><KeyRound size={12} /> {t.app.login.twofa_hint}</p>
                </div>
                {error && <div data-testid="totp-error" className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
                <Button type="submit" data-testid="totp-submit" disabled={submitting || twofa.length < 4} className="w-full h-12 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
                  {submitting ? t.app.login.twofa_verifying : t.app.login.twofa_verify}
                </Button>
                <button type="button" onClick={() => { setStep(1); setError(""); }} className="w-full text-sm text-[#5C6B5C] hover:text-[#1C3A27]" data-testid="totp-back">
                  {t.app.login.twofa_back}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
